import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, X } from "lucide-react";
import api, { mediaSrc } from "@/lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const SPANS = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4 md:row-span-2",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-12",
];

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/media", { params: { kind: "photo" } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(items.map((i) => i.category).filter(Boolean))];
    return ["All", ...cats];
  }, [items]);

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="work" data-testid="gallery-section" className="relative py-28 sm:py-36">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
          <SectionHeading eyebrow="Selected Work" title="Featured Frames" testid="gallery-heading" />
          <Reveal delay={0.15} className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-2.5 border transition-all duration-300 ${
                  filter === c
                    ? "bg-ivory text-ink border-ivory"
                    : "border-line text-ash hover:text-ivory hover:border-ivory/40"
                }`}
              >
                {c}
              </button>
            ))}
          </Reveal>
        </div>

        {items.length === 0 ? (
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-mute border border-dashed border-line p-16 text-center">
            Frames arriving soon — new work is being developed in the darkroom.
          </p>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-3">
            <AnimatePresence mode="popLayout">
              {visible.map((item, i) => (
                <motion.button
                  layout
                  key={item.id}
                  data-testid={`gallery-item-card-${i}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => setSelected(item)}
                  className={`group relative overflow-hidden border border-ivory/10 text-left ${SPANS[i % SPANS.length]}`}
                >
                  <img
                    src={mediaSrc(item)}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/10 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
                  {item.featured && (
                    <span
                      data-testid={`gallery-item-featured-${i}`}
                      className="absolute top-4 left-4 flex items-center gap-1.5 font-mono text-[8px] tracking-[0.3em] uppercase text-amber bg-ink/70 backdrop-blur px-2.5 py-1.5 border border-amber/30"
                    >
                      <Star size={9} className="fill-amber" /> Featured
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-amber">
                      {String(i + 1).padStart(2, "0")} — {item.category}
                    </p>
                    <h3 className="font-display text-lg sm:text-xl text-ivory mt-1.5 tracking-wide">
                      {item.title}
                    </h3>
                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ash mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {[item.location, item.specs].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md flex items-center justify-center p-5 sm:p-14"
          >
            <button
              data-testid="gallery-lightbox-close"
              aria-label="Close"
              onClick={() => setSelected(null)}
              className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center border border-ivory/20 text-ivory hover:border-amber hover:text-amber transition-colors z-10"
            >
              <X size={16} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              <img
                src={mediaSrc(selected)}
                alt={selected.title}
                className="w-full max-h-[74vh] object-contain border border-ivory/10"
              />
              <figcaption className="flex flex-wrap items-baseline justify-between gap-3 mt-4">
                <span className="font-display text-lg text-ivory tracking-wide">{selected.title}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ash">
                  {[selected.location, selected.specs].filter(Boolean).join(" · ")}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
